import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit3, Package, Search, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import { api } from "@/lib/api";
import { formatEuro } from "@/lib/constants";

const CATEGORY_COLORS = {
  Horeca: "#E85D04",
  Retail: "#2A9D8F",
  "Dry Chiles": "#7B6DCC",
  "Corn Flour": "#C9A02A",
};

const blank = () => ({
  sku: "",
  name: "",
  family: "",
  format: "",
  category: "Horeca",
  batch_prefix: "",
  pricing_tiers: { standard: 0, partner1: 0, partner2: 0 },
  retail_price: 0,
  active: true,
  notes: "",
});

export default function Products() {
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const load = async () => {
    const [p, m] = await Promise.all([api.listProducts(), api.getMeta()]);
    setProducts(p);
    setMeta(m);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const s = search.toLowerCase();
      const match = !s
        || p.name?.toLowerCase().includes(s)
        || p.sku?.toLowerCase().includes(s)
        || p.family?.toLowerCase().includes(s);
      const cat = catFilter === "all" || p.category === catFilter;
      return match && cat;
    });
  }, [products, search, catFilter]);

  const openCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...blank(),
      ...p,
      pricing_tiers: p.pricing_tiers || { standard: 0, partner1: 0, partner2: 0 },
      retail_price: p.retail_price ?? 0,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.sku || !form.name) return toast.error("SKU and Name are required");
    const payload = { ...form };
    // Strip pricing model not relevant to category
    if (form.category === "Horeca") {
      payload.retail_price = null;
    } else {
      payload.pricing_tiers = null;
    }
    try {
      if (editing) {
        await api.updateProduct(editing.id, payload);
        toast.success("Product updated");
      } else {
        await api.createProduct(payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save product");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Inventory row will also be removed.`)) return;
    await api.deleteProduct(p.id);
    toast.success("Product deleted");
    load();
  };

  const seedDefaults = async () => {
    try {
      const r = await api.seedDefaultProducts();
      toast.success(`Seeded ${r.inserted} default product(s)`);
      load();
    } catch { toast.error("Failed to seed defaults"); }
  };

  if (!meta) return <div className="text-sm text-[#6C757D]">Loading…</div>;

  return (
    <div data-testid="page-products">
      <PageHeader
        overline="Catalog"
        title="Products"
        subtitle="Multi-format SKUs with category-aware pricing. Horeca uses 3-tier partner pricing; Retail uses a single price."
        actions={
          <div className="flex gap-2">
            <ExportButton module="products" />
            <Button variant="outline" onClick={seedDefaults} data-testid="seed-default-products">
              <RefreshCw size={16} className="mr-2" /> Seed Defaults
            </Button>
            <Button onClick={openCreate} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-product-btn">
              <Plus size={16} className="mr-2" /> New Product
            </Button>
          </div>
        }
      />

      <div className="card-soft p-4 mb-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU, name or family"
            className="pl-9 bg-white border-[#E5E5E5]"
            data-testid="product-search"
          />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-full sm:w-52 bg-white border-[#E5E5E5]" data-testid="product-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {meta.product_categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No products yet"
          description="Add a product or click Seed Defaults to load the 8 Garnacha SKUs."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <div key={p.id} className="card-soft card-soft-hover p-5" data-testid={`product-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full font-bold"
                      style={{ backgroundColor: `${CATEGORY_COLORS[p.category] || "#6C757D"}1f`, color: CATEGORY_COLORS[p.category] || "#6C757D" }}
                    >
                      {p.category}
                    </span>
                    {!p.active && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E5E5E5] text-[#6C757D] font-bold">Inactive</span>
                    )}
                  </div>
                  <h3 className="font-display font-semibold text-lg text-[#222] mt-1">{p.name}</h3>
                  <div className="font-mono text-xs text-[#6C757D]">{p.sku} · {p.format || "—"}</div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>
                    <Edit3 size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => del(p)} className="text-[#E76F51] hover:bg-[#E76F51]/10" data-testid={`delete-product-${p.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>

              {p.category === "Horeca" && p.pricing_tiers ? (
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <PriceTile label="Standard" color="#6C757D" value={p.pricing_tiers.standard} />
                  <PriceTile label="Partner 1" color="#E85D04" value={p.pricing_tiers.partner1} />
                  <PriceTile label="Partner 2" color="#C9A02A" value={p.pricing_tiers.partner2} />
                </div>
              ) : (
                <div className="mt-4">
                  <PriceTile label="Retail Price" color="#2A9D8F" value={p.retail_price} wide />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="SKU *">
              <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="font-mono text-sm" data-testid="input-product-sku" />
            </Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="input-product-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.product_categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Product Name *" className="col-span-2">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="input-product-name" />
            </Field>
            <Field label="Product Family">
              <Input value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value })}
                placeholder="e.g. Verde Habanero" data-testid="input-product-family" />
            </Field>
            <Field label="Format">
              <Input value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}
                placeholder="e.g. 2.5 L, 250 ml, 1 kg" data-testid="input-product-format" />
            </Field>
            <Field label="Batch Prefix">
              <Input value={form.batch_prefix} onChange={(e) => setForm({ ...form, batch_prefix: e.target.value.toUpperCase() })}
                placeholder="VH, MH, VT…" className="font-mono" data-testid="input-product-batch-prefix" />
            </Field>
            <Field label="Active">
              <div className="flex items-center gap-3 h-10">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="input-product-active" />
                <span className="text-sm">{form.active ? "Active" : "Inactive"}</span>
              </div>
            </Field>

            {form.category === "Horeca" ? (
              <Field label="Horeca Pricing — 3-tier (€)" className="col-span-2">
                <div className="grid grid-cols-3 gap-2">
                  <TierInput label="Standard" testId="input-price-standard"
                    value={form.pricing_tiers.standard}
                    onChange={(v) => setForm({ ...form, pricing_tiers: { ...form.pricing_tiers, standard: v } })} />
                  <TierInput label="Partner 1" testId="input-price-partner1"
                    value={form.pricing_tiers.partner1}
                    onChange={(v) => setForm({ ...form, pricing_tiers: { ...form.pricing_tiers, partner1: v } })} />
                  <TierInput label="Partner 2" testId="input-price-partner2"
                    value={form.pricing_tiers.partner2}
                    onChange={(v) => setForm({ ...form, pricing_tiers: { ...form.pricing_tiers, partner2: v } })} />
                </div>
              </Field>
            ) : (
              <Field label="Retail Price (€)" className="col-span-2">
                <Input
                  type="number" step="0.01" min="0"
                  value={form.retail_price === 0 ? "" : form.retail_price}
                  onChange={(e) => setForm({ ...form, retail_price: e.target.value === "" ? "" : Number(e.target.value) })}
                  data-testid="input-retail-price"
                />
                <span className="text-[10px] text-[#6C757D]">No partner discount applies for non-Horeca products.</span>
              </Field>
            )}

            <Field label="Notes" className="col-span-2">
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-product">Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-product">
              {editing ? "Save Changes" : "Create Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PriceTile = ({ label, color, value, wide }) => (
  <div className={`rounded-lg px-3 py-2 ${wide ? "" : ""}`} style={{ backgroundColor: `${color}10`, border: `1px solid ${color}33` }}>
    <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>{label}</div>
    <div className="font-display font-bold text-base text-[#222]">{formatEuro(value)}</div>
  </div>
);

const TierInput = ({ label, value, onChange, testId }) => (
  <div className="flex flex-col gap-1">
    <Label className="text-[10px] uppercase tracking-wider text-[#6C757D] font-semibold">{label}</Label>
    <Input type="number" step="0.01" min="0" value={value === 0 ? "" : value} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testId} />
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
